// ENG-5858: pure decision function — should the manager restart an agent's
// persistent session because the effective sender_policy changed?
//
// Background: the ENG-5836 epic wires sender_policy through /host/refresh →
// manager → writeChannelCredentials → .mcp.json env vars. The manager's
// CHANNEL_WRITE_VERSION hash correctly invalidates on a policy change and
// the adapter rewrites the file. The gap that this decision closes is the
// last hop: the slack-channel / teams-channel MCP child reads
// SLACK_SENDER_POLICY / MSTEAMS_SENDER_POLICY from process.env exactly
// once at boot, so a rewrite of .mcp.json on disk doesn't reach the
// running child. Two existing restart paths refuse to help:
//
//   1. decideChannelRestart (channel-restart-decision.ts) only fires on
//      channel set membership change. Same channel + new env → no restart.
//   2. The .mcp.json value-only drift watcher (manager-worker.ts ~1029)
//      explicitly suppresses restart on value-only drift to stop Composio
//      URL rotation flapping the breaker (ENG-5537).
//
// Both suppressions are correct for their original purpose. This decision
// adds an orthogonal trigger keyed on the sender_policy hash itself, so
// composio URL rotation can't accidentally fire it (the hash sees only
// sender_policy, not the wider channel config or .mcp.json).
//
// Pure helper for unit testability, mirroring decideChannelRestart.

export interface SenderPolicyRestartDecisionInput {
  /**
   * Hash of the effective sender_policy from the *previous* refresh
   * tick, or `undefined` on first poll for this agent.
   *
   * ENG-7771: "first poll" is NOT a safe no-op. The baseline used to be
   * in-memory only, so every manager restart (= every deploy) looked like
   * a first poll while the agent's tmux session survived with its
   * boot-time env — a policy changed across a manager restart was never
   * delivered and never self-healed. The baseline is now hydrated from
   * the on-disk sidecar (sender-policy-baseline.ts), so a genuine
   * `undefined` means the manager truly does not know what the running
   * session enforces (transition deploy, lost/corrupt sidecar, brand-new
   * agent). For a restrictive policy that unknown is unsafe — see
   * `currentPolicyRestrictive`.
   */
  previousHash: string | undefined;
  /**
   * Whether the current effective policy actually restricts senders
   * (`isSenderPolicyRestrictive`). Governs the first-poll branch only:
   * unknown baseline + restrictive policy on a live session ⇒ restart
   * (fail closed — bounded restart cost vs unbounded exposure); unknown
   * baseline + non-restrictive policy ⇒ establish baseline without a
   * restart (no exposure; avoids a fleet-wide respawn on the deploy that
   * ships the sidecar).
   */
  currentPolicyRestrictive: boolean;
  /**
   * Hash of the effective sender_policy from this refresh tick. May be
   * the literal string of `null` (canonicalised) — that's a meaningful
   * value (org/agent cleared their policy) and must compare equal to
   * itself on subsequent ticks.
   */
  currentHash: string;
  /**
   * Agent's `session_mode` from the refresh response. Restart only
   * applies to `persistent` sessions — oneshot agents have no long-
   * lived MCP child whose env needs refreshing.
   */
  sessionMode: string | undefined;
  /**
   * Agent's framework. The sender_policy env vars are consumed by
   * MCP servers launched through Claude Code's .mcp.json mechanism;
   * other frameworks (openclaw, nemoclaw) don't use that path and
   * the restart doesn't apply.
   */
  framework: string;
  /**
   * Whether the agent's tmux session is currently alive. If it isn't,
   * there's nothing to restart — the manager will spawn a fresh one
   * downstream which reads the freshly-written .mcp.json env.
   */
  sessionHealthy: boolean;
  /**
   * Whether a channel-set restart was already scheduled for this agent
   * on this same tick. If so, that restart will pick up the new
   * sender_policy env for free — scheduling a second one would just
   * coalesce in `scheduleSessionRestart` and overwrite the friendlier
   * "channels added/removed" notice with a less informative one.
   */
  channelSetRestartAlreadyScheduled: boolean;
}

export interface SenderPolicyRestartDecision {
  /** True iff the manager should restart the session. */
  restart: boolean;
  /** Set to true when previousHash was undefined (informational). */
  firstPoll: boolean;
  /**
   * Set to true when a KNOWN previous hash differs from the current one.
   * Always false on first poll — an unknown baseline is not a change, and
   * the two cases restart for different reasons (delivery of a change vs
   * the ENG-7771 fail-safe verify), which the caller words differently.
   */
  changed: boolean;
}

export function decideSenderPolicyRestart(
  input: SenderPolicyRestartDecisionInput,
): SenderPolicyRestartDecision {
  const {
    previousHash,
    currentPolicyRestrictive,
    currentHash,
    sessionMode,
    framework,
    sessionHealthy,
    channelSetRestartAlreadyScheduled,
  } = input;

  const firstPoll = previousHash === undefined;

  // No change — debounces same-policy hashes that recompute every tick.
  if (!firstPoll && previousHash === currentHash) {
    return { restart: false, firstPoll: false, changed: false };
  }

  // First poll with a non-restrictive policy — establish baseline, never
  // restart. There is nothing a stale env could be exposing (the desired
  // gate admits everyone the channel admits), so the unknown running state
  // is harmless and a restart would be pure churn.
  if (firstPoll && !currentPolicyRestrictive) {
    return { restart: false, firstPoll: true, changed: false };
  }

  const changed = !firstPoll;

  // Restart only applies to live persistent claude-code sessions. A dead
  // session (or a not-yet-spawned one — the poll spawns AFTER this check)
  // reads the freshly written env at its next spawn, so there is nothing
  // to deliver; oneshot sessions have no long-lived MCP child; other
  // frameworks don't consume the policy env vars.
  if (sessionMode !== 'persistent') {
    return { restart: false, firstPoll, changed };
  }
  if (framework !== 'claude-code') {
    return { restart: false, firstPoll, changed };
  }
  if (!sessionHealthy) {
    return { restart: false, firstPoll, changed };
  }

  // A channel-set restart already scheduled this tick will respawn the
  // MCP children with the freshly-written env — no second restart needed.
  if (channelSetRestartAlreadyScheduled) {
    return { restart: false, firstPoll, changed };
  }

  // Either the policy changed, or (ENG-7771 fail-safe) this is a first poll
  // with a restrictive policy and a live session whose enforcement state is
  // unknown — restart so the security control is guaranteed delivered.
  return { restart: true, firstPoll, changed };
}
